import { FC } from 'react';
import MuiDialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Divider from '@mui/material/Divider';
import { DialogHeader } from './DialogHeader';
import { DialogFooter } from './DialogFooter';
import { DialogComponentProps } from './type';
import { DraggablePaper } from './DraggablePaper';

export const DRAGGABLE_DIALOG_ID = 'draggable-dialog';

export const Dialog: FC<DialogComponentProps> = ({
  open,
  title,
  variant = 'primary',
  dialogTitle,
  dialogHeader,
  dialogContent,
  dialogFooter,
  width,
  height,
  maxWidth = 'sm',
  maxHeight,
  dialogContentStyles,
  dialogActionStyles,
  dialogStyles,
  showHeaderDivider = true,
  showFooterDivider = true,
  submitButtonLabel,
  cancelButtonLabel,
  onClose,
  onSubmit,
  onCancel,
  hideCloseButton = false,
  hideCancelButton = false,
  showFooter = true,
  PaperComponent,
  isDraggable = false,
  isLoading,
  titleColor,
  fullScreen = false,
  disabled = false,
  showHeader = true,
  borderRadius = '4px',
  titleVariant,
  closeOnBackdropClick = false,
  stopContentClickPropagation = true,
  dataTestId,
}) => {
  if (!open) return null;
  return (
    <MuiDialog
      open={open}
      maxWidth={maxWidth}
      onClose={(_, reason) => {
        if (reason === 'backdropClick' && !closeOnBackdropClick) {
          return;
        }
        onClose?.();
      }}
      fullWidth
      PaperComponent={isDraggable ? DraggablePaper : PaperComponent}
      PaperProps={{
        style: {
          width: width ?? 'auto',
          height: height ?? 'auto',
          maxHeight: maxHeight ?? 'none',
        },
        'data-testid': dataTestId,
      }}
      sx={{
        zIndex: '9999 !important',
        '& .MuiDialog-paper': {
          borderRadius: borderRadius,
        },
        ...dialogStyles,
      }}
      fullScreen={fullScreen}
    >
      {dialogHeader ?? (
        <DialogHeader
          showHeader={showHeader}
          variant={variant}
          isDraggable={isDraggable}
          dialogTitle={dialogTitle}
          title={title}
          hideCloseButton={hideCloseButton}
          onClose={onClose}
          titleColor={titleColor}
          titleVariant={titleVariant}
        />
      )}

      {showHeaderDivider && <Divider />}

      <DialogContent
        sx={dialogContentStyles}
        {...(stopContentClickPropagation && {
          onClick: (e) => e.stopPropagation(),
        })}
      >
        {dialogContent}
      </DialogContent>

      {showFooterDivider && showFooter && <Divider />}

      {showFooter && (
        <DialogActions
          sx={dialogActionStyles}
          {...(stopContentClickPropagation && {
            onClick: (e) => e.stopPropagation(),
          })}
        >
          {dialogFooter || (
            <DialogFooter
              onClose={onCancel || onClose}
              onSubmit={onSubmit}
              variant={variant}
              isLoading={isLoading ?? false}
              submitButtonLabel={submitButtonLabel}
              cancelButtonLabel={cancelButtonLabel}
              disabled={disabled}
              hideCancelButton={hideCancelButton}
            />
          )}
        </DialogActions>
      )}
    </MuiDialog>
  );
};
